export type GuideInduction = "naturally_aspirated" | "forced_induction";
export type GuideFuel = "petrol" | "e85" | "methanol";
export type GuidePowerUnit = "FWKW" | "RWKW" | "AWKW";

export interface GuideCatalogueProduct {
  sku: string;
  title: string | null;
  url: string | null;
  availabilityStatus: string | null;
}

/** A catalogue suffix such as -6 denotes a pack; other suffixes are part numbers. */
export function cataloguePackQuantity(requestedSku: string, catalogueSku: string): number | null {
  const requested = requestedSku.trim().toUpperCase();
  const candidate = catalogueSku.trim().toUpperCase();
  if (candidate === requested) return 0;
  if (!candidate.startsWith(`${requested}-`)) return null;
  const suffix = candidate.slice(requested.length + 1);
  if (!/^[0-9]+$/.test(suffix)) return null;
  const quantity = Number(suffix);
  return Number.isSafeInteger(quantity) && quantity > 0 ? quantity : null;
}

export function catalogueProductsForSku(
  requestedSku: string,
  candidates: GuideCatalogueProduct[],
): GuideCatalogueProduct[] {
  return candidates
    .filter((candidate) => cataloguePackQuantity(requestedSku, candidate.sku) !== null)
    .sort((left, right) =>
      (cataloguePackQuantity(requestedSku, left.sku) ?? Infinity)
      - (cataloguePackQuantity(requestedSku, right.sku) ?? Infinity)
      || left.sku.localeCompare(right.sku));
}

export interface GuideAccessoryRequirement {
  kind: "required" | "any_of";
  skus: string[];
  label: string;
}

export interface GuideOptionRow {
  id: number;
  flowCc: number;
  injectorSku: string;
  accessoryText: string;
  accessorySkus: string[];
  accessoryRequirements: GuideAccessoryRequirement[];
  note: string;
  maxPowerKw: number;
  powerUnit: GuidePowerUnit;
}

export interface GuideRecommendation extends GuideOptionRow {
  adjustedMaxPowerKw: number;
  headroomKw: number;
  suitable: boolean;
}

export function pressureAdjustmentPercent(
  induction: GuideInduction,
  basePressureBar: number,
  boostBar: number,
): number {
  const baseAdjustment = Math.max(0, basePressureBar - 3) * 10;
  const boostAdjustment = induction === "forced_induction" ? Math.max(0, boostBar) * 10 : 0;
  return baseAdjustment + boostAdjustment;
}

export function selectGuideRecommendations(
  options: GuideOptionRow[],
  targetPowerKw: number,
  adjustmentPercent: number,
): GuideRecommendation[] {
  const ranked = options
    .map((option) => {
      const adjustedMaxPowerKw = option.maxPowerKw * (1 + adjustmentPercent / 100);
      return {
        ...option,
        adjustedMaxPowerKw,
        headroomKw: adjustedMaxPowerKw - targetPowerKw,
        suitable: adjustedMaxPowerKw >= targetPowerKw,
      };
    })
    .sort((left, right) => left.adjustedMaxPowerKw - right.adjustedMaxPowerKw || left.flowCc - right.flowCc);
  const closestSuitable = ranked.find((option) => option.suitable);
  return closestSuitable ? [closestSuitable] : [];
}
